<template>
  <sui-segment-group style="max-width: 760px; margin: auto" raised>
    <sui-segment inverted style="text-align: center; font-size: 18px">
      <sui-icon name="clock outline" /> Reglas de recordatorios de pago
    </sui-segment>

    <sui-segment :loading="loading">
      <sui-message info>
        Las reglas se ejecutan en horario de America/Mexico_City y solo contactan numeros con WhatsApp habilitado.
      </sui-message>

      <div class="rule-card">
        <div class="rule-heading">
          <div>
            <h3>Pago proximo</h3>
            <p>Envia un recordatorio una vez en cada dia seleccionado antes del vencimiento.</p>
          </div>
          <SuiCheckbox toggle v-model="rules.payment_due.is_enabled" />
        </div>

        <sui-form>
          <sui-form-field>
            <label>Dias antes del vencimiento</label>
            <input v-model="rules.payment_due.due_offsets" type="text" placeholder="Ej: 7, 3, 1" />
            <div class="help-text">Separa los dias con comas. El valor 0 envia el dia del vencimiento.</div>
          </sui-form-field>

          <div class="two-column-grid">
            <sui-form-field>
              <label>Hora de envio</label>
              <select v-model.number="rules.payment_due.send_hour" class="ui dropdown">
                <option v-for="hour in hours" :key="hour" :value="hour">{{ formatNumber(hour) }}</option>
              </select>
            </sui-form-field>
            <sui-form-field>
              <label>Minuto</label>
              <select v-model.number="rules.payment_due.send_minute" class="ui dropdown">
                <option v-for="minute in minutes" :key="minute" :value="minute">{{ formatNumber(minute) }}</option>
              </select>
            </sui-form-field>
          </div>

          <sui-form-field>
            <label>Plantilla</label>
            <select v-model.number="rules.payment_due.template_id" class="ui dropdown">
              <option :value="null">Selecciona una plantilla</option>
              <option v-for="template in templatesFor('payment_due')" :key="template.template_id" :value="template.template_id">
                {{ template.template_name }}
              </option>
            </select>
          </sui-form-field>
        </sui-form>

        <div class="rule-summary">{{ dueRuleSummary }}</div>
        <sui-button basic size="small" :loading="runningRule === 'payment_due'" :disabled="!rules.payment_due.is_enabled" @click="runRule('payment_due')">
          Ejecutar ahora
        </sui-button>
      </div>

      <div class="rule-card">
        <div class="rule-heading">
          <div>
            <h3>Pago vencido</h3>
            <p>Repite el recordatorio mientras el pago continue vencido.</p>
          </div>
          <SuiCheckbox toggle v-model="rules.payment_overdue.is_enabled" />
        </div>

        <sui-form>
          <div class="two-column-grid">
            <sui-form-field>
              <label>Iniciar despues de</label>
              <div class="ui right labeled input">
                <input v-model.number="rules.payment_overdue.overdue_start_day" type="number" min="1" max="365" />
                <div class="ui basic label">dia(s) vencido</div>
              </div>
            </sui-form-field>
            <sui-form-field>
              <label>Repetir cada</label>
              <div class="ui right labeled input">
                <input v-model.number="rules.payment_overdue.overdue_repeat_days" type="number" min="1" max="365" />
                <div class="ui basic label">dia(s)</div>
              </div>
            </sui-form-field>
          </div>

          <div class="two-column-grid">
            <sui-form-field>
              <label>Hora de envio</label>
              <select v-model.number="rules.payment_overdue.send_hour" class="ui dropdown">
                <option v-for="hour in hours" :key="hour" :value="hour">{{ formatNumber(hour) }}</option>
              </select>
            </sui-form-field>
            <sui-form-field>
              <label>Minuto</label>
              <select v-model.number="rules.payment_overdue.send_minute" class="ui dropdown">
                <option v-for="minute in minutes" :key="minute" :value="minute">{{ formatNumber(minute) }}</option>
              </select>
            </sui-form-field>
          </div>

          <sui-form-field>
            <label>Plantilla</label>
            <select v-model.number="rules.payment_overdue.template_id" class="ui dropdown">
              <option :value="null">Selecciona una plantilla</option>
              <option v-for="template in templatesFor('payment_overdue')" :key="template.template_id" :value="template.template_id">
                {{ template.template_name }}
              </option>
            </select>
          </sui-form-field>
        </sui-form>

        <div class="rule-summary">{{ overdueRuleSummary }}</div>
        <sui-button basic size="small" :loading="runningRule === 'payment_overdue'" :disabled="!rules.payment_overdue.is_enabled" @click="runRule('payment_overdue')">
          Ejecutar ahora
        </sui-button>
      </div>
    </sui-segment>

    <sui-segment>
      <sui-message v-if="errors.length" error>
        <sui-message-item v-for="error in errors" :key="error">{{ error }}</sui-message-item>
      </sui-message>
      <sui-button positive :loading="saving" @click="saveRules">
        <sui-icon name="save" /> Guardar reglas
      </sui-button>
    </sui-segment>
  </sui-segment-group>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { SuiCheckbox } from "vue-fomantic-ui";
import { useToast } from "vue-toastification";
import API from "../../api/Api";

const toast = useToast();
const loading = ref(false);
const saving = ref(false);
const runningRule = ref("");
const templates = ref([]);
const errors = ref([]);
const hours = Array.from({ length: 24 }, (_, index) => index);
const minutes = Array.from({ length: 60 }, (_, index) => index);

const rules = reactive({
  payment_due: {
    event_type: "payment_due",
    is_enabled: false,
    send_hour: 9,
    send_minute: 0,
    due_offsets: "3",
    overdue_start_day: 1,
    overdue_repeat_days: 1,
    template_id: null,
  },
  payment_overdue: {
    event_type: "payment_overdue",
    is_enabled: false,
    send_hour: 9,
    send_minute: 0,
    due_offsets: "3",
    overdue_start_day: 1,
    overdue_repeat_days: 3,
    template_id: null,
  },
});

const formatNumber = (value) => String(value).padStart(2, "0");

const templatesFor = (eventType) =>
  templates.value.filter((template) => template.event_type === eventType && template.is_enabled === 1);

const parseOffsets = (value) => {
  const values = String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const offsets = values.map(Number);
  if (!values.length || offsets.some((offset) => !Number.isInteger(offset) || offset < 0 || offset > 365)) {
    return null;
  }
  return [...new Set(offsets)].sort((a, b) => b - a);
};

const dueRuleSummary = computed(() => {
  const offsets = parseOffsets(rules.payment_due.due_offsets);
  if (!offsets) return "Ingresa dias validos entre 0 y 365.";
  const days = offsets.join(", ");
  return `Se enviara una vez ${offsets.length > 1 ? "en los dias" : "el dia"} ${days} antes del vencimiento, a las ${formatNumber(rules.payment_due.send_hour)}:${formatNumber(rules.payment_due.send_minute)}.`;
});

const overdueRuleSummary = computed(() =>
  `Se enviara desde el dia ${rules.payment_overdue.overdue_start_day} de atraso y cada ${rules.payment_overdue.overdue_repeat_days} dia(s), a las ${formatNumber(rules.payment_overdue.send_hour)}:${formatNumber(rules.payment_overdue.send_minute)}.`,
);

const applyRules = (savedRules) => {
  for (const rule of savedRules || []) {
    const formRule = rules[rule.event_type];
    if (!formRule) continue;
    Object.assign(formRule, rule, {
      is_enabled: !!rule.is_enabled,
      due_offsets: Array.isArray(rule.due_offsets) ? rule.due_offsets.join(", ") : rule.due_offsets,
    });
  }
};

const load = async () => {
  loading.value = true;
  try {
    const [rulesResponse, templatesResponse] = await Promise.all([
      API.getWhatsAppReminderRules(),
      API.getWhatsAppTemplates("payments"),
    ]);
    if (rulesResponse.error) throw new Error(rulesResponse.message_client || "No se pudieron cargar las reglas.");
    if (templatesResponse.error) throw new Error(templatesResponse.message_client || "No se pudieron cargar las plantillas.");
    templates.value = templatesResponse.data || [];
    applyRules(rulesResponse.data);
  } catch (error) {
    toast.error(error.message || "No se pudieron cargar las reglas de recordatorio.");
  } finally {
    loading.value = false;
  }
};

const validate = () => {
  errors.value = [];
  const dueOffsets = parseOffsets(rules.payment_due.due_offsets);
  if (!dueOffsets) errors.value.push("Los dias antes del vencimiento deben ser numeros entre 0 y 365.");

  for (const rule of Object.values(rules)) {
    if (!Number.isInteger(rule.send_hour) || rule.send_hour < 0 || rule.send_hour > 23) {
      errors.value.push("La hora de envio debe estar entre 0 y 23.");
    }
    if (!Number.isInteger(rule.send_minute) || rule.send_minute < 0 || rule.send_minute > 59) {
      errors.value.push("El minuto de envio debe estar entre 0 y 59.");
    }
    if (!rule.template_id) errors.value.push("Selecciona una plantilla para cada regla.");
  }

  if (!Number.isInteger(rules.payment_overdue.overdue_start_day) || rules.payment_overdue.overdue_start_day < 1) {
    errors.value.push("El inicio de vencimiento debe ser de al menos un dia.");
  }
  if (!Number.isInteger(rules.payment_overdue.overdue_repeat_days) || rules.payment_overdue.overdue_repeat_days < 1) {
    errors.value.push("La repeticion de vencimiento debe ser de al menos un dia.");
  }
  return errors.value.length === 0;
};

const serializeRules = () => {
  const dueOffsets = parseOffsets(rules.payment_due.due_offsets);
  return [
    { ...rules.payment_due, due_offsets: dueOffsets },
    { ...rules.payment_overdue, due_offsets: [] },
  ];
};

const saveRules = async () => {
  if (!validate()) return;
  saving.value = true;
  try {
    const response = await API.saveWhatsAppReminderRules(serializeRules());
    if (response.error) {
      toast.error(response.message_client || "No se pudieron guardar las reglas.");
      return;
    }
    applyRules(response.data);
    toast.success("Reglas de recordatorio guardadas.");
  } catch (error) {
    toast.error("No se pudieron guardar las reglas de recordatorio.");
  } finally {
    saving.value = false;
  }
};

const runRule = async (eventType) => {
  if (!validate()) return;
  runningRule.value = eventType;
  try {
    const response = await API.runWhatsAppReminderRule(eventType);
    if (response.error) {
      toast.error(response.message_client || "No se pudo ejecutar la regla.");
      return;
    }
    toast.success(`Ejecucion terminada. Mensajes enviados: ${response.data?.sent || 0}.`);
  } catch (error) {
    toast.error("No se pudo ejecutar la regla de recordatorio.");
  } finally {
    runningRule.value = "";
  }
};

onMounted(load);
</script>

<style scoped>
.rule-card {
  border: 1px solid #dfe6eb;
  border-radius: 8px;
  padding: 18px;
  margin-top: 16px;
}

.rule-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;
}

.rule-heading h3 {
  margin: 0 0 4px;
}

.rule-heading p,
.help-text {
  color: #6b7280;
  font-size: 13px;
  margin: 0;
}

.help-text {
  margin-top: 5px;
}

.two-column-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.rule-summary {
  background: #f3f8fc;
  border-left: 3px solid #2185d0;
  color: #334155;
  font-size: 13px;
  line-height: 1.45;
  margin: 14px 0 10px;
  padding: 9px 11px;
}

@media (max-width: 600px) {
  .two-column-grid {
    grid-template-columns: 1fr;
  }
}
</style>
